import { Grid } from '@mui/material'
import React, { useMemo } from 'react'
import {
  AutocompleteArrayInput,
  BooleanInput,
  DateInput,
  DateTimeInput,
  FormDataConsumer,
  ReferenceArrayInput,
  required,
  SelectArrayInput,
  SimpleForm,
  TextInput,
  useLocale,
  useRecordContext,
} from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { AutocompleteTextInput, CreateCustomer, SelectionInput } from '~/components'
import { customerName, type JoinField, nhost, toJoinRows, withJoinIds } from '~/helpers'
import './UpsertAppointment.css'

const endValidation = (value, allValues) => {
  if (!value || !allValues.start) {
    return 'Event has no duration'
  }
  // The values are ISO strings (or Dates): compare the instants. (Subtracting
  // the strings was NaN, so an appointment ending as it began went through.)
  if (Date.parse(value) - Date.parse(allValues.start) <= 0) {
    return 'validation.startEnd'
  }
  return undefined
}

/**
 * A time as the instant it means, with its offset: what the save needs.
 * In the form, a time is what the input holds — "2026-11-04T15:00", the wall
 * clock here, no zone — and Postgres reads a timestamptz without an offset as
 * UTC: 15:00 in Kappeln was stored as 15:00 UTC and shown as 16:00 (17:00 in
 * summer). Converted on saving only; inside the form the value stays what the
 * input shows, as react-admin's <DateTimeInput> expects — holding the instant
 * there instead made it re-feed the input around every edit, and Safari
 * refused the field ("Ungültiger Wert").
 */
export const toInstant = (value: unknown) => {
  if (value == null || value === '') return value
  const date = value instanceof Date ? value : new Date(String(value))
  return isNaN(date.getTime()) ? value : date.toISOString()
}

const pad = (n: number) => String(n).padStart(2, '0')

/** A Date as a datetime-local value: the wall clock here, as the input shows it. */
const toLocalInput = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`

const titleValidation = (value, allValues) => {
  if (!value && !allValues.type) {
    return 'ra.validation.required'
  }
  return undefined
}

/**
 * react-admin 5's <DateInput>/<DateTimeInput> hand `onChange` the DOM change
 * event, where v3's picker handed over a Date — calling .getTime() on the event
 * threw as soon as anyone edited a start or end time. `valueAsDate` is null for
 * datetime-local inputs, so parse the string value instead.
 */
const dateFromChangeEvent = (event: any): Date | null => {
  const raw = event?.target?.value
  if (!raw) return null
  const parsed = new Date(raw)
  return isNaN(parsed.getTime()) ? null : parsed
}

export const APPOINTMENT_JOINS: JoinField[] = [
  { rows: 'appointment_boat', key: 'boat_id', ids: 'boat_ids' },
  { rows: 'appointment_customer', key: 'customer_id', ids: 'customer_ids' },
  // Whose calendar it is in (the calendar sync shows it there).
  { rows: 'appointment_user', key: 'user_id', ids: 'user_ids' },
]

const toRows = toJoinRows(APPOINTMENT_JOINS)

/**
 * Every appointment save goes through here (Create/Edit `transform`, the
 * appointment tabs' dialog): the form's id lists back into join rows, and the
 * times into instants. An all-day appointment keeps its date as entered.
 */
export const appointmentRelations = (data: Record<string, any>) => {
  const out = toRows(data)
  if (!out.allDay) {
    out.start = toInstant(out.start)
    out.end = toInstant(out.end)
  }
  return out
}

/**
 * The form for an appointment, opened with id lists for its boats, customers
 * and the people it belongs to — a new one belongs to whoever makes it.
 */
export const AppointmentForm = () => {
  const record = useRecordContext()
  const formRecord = useMemo(() => {
    const withIds = withJoinIds(record, APPOINTMENT_JOINS)
    if (record?.id) return withIds
    const me = nhost.getUserSession()?.user?.id
    return { ...withIds, user_ids: withIds?.user_ids?.length ? withIds.user_ids : me ? [me] : [] }
  }, [record])
  return (
    // The form checks its fields itself, with messages that say what is wrong;
    // the browser's own check only said "Ungültiger Wert" and blocked the save.
    <SimpleForm record={formRecord} warnWhenUnsavedChanges noValidate>
      <UpsertAppointment />
    </SimpleForm>
  )
}

export const UpsertAppointment = () => {
  const locale = useLocale()
  const validateStart = [required()]
  const validateEnd = [required(), endValidation]
  const validateTitle = [titleValidation]

  const hour = 60 * 60 * 1000
  const { setValue, getValues } = useFormContext()
  const values = getValues()
  // How long it lasts; an appointment without a length (or a broken one) gets an hour.
  const [duration, setDuration] = React.useState(() => {
    const length = Date.parse(values.end) - Date.parse(values.start)
    return length > 0 ? length : hour
  })

  const handleStartChange = (event: any) => {
    const value = dateFromChangeEvent(event)
    if (!value) return
    const end = new Date(value.getTime() + duration)
    // In the input's own terms: a date for all-day, the local wall clock otherwise.
    setValue('end', getValues('allDay') ? end : toLocalInput(end))
  }

  const handleEndChange = (event: any) => {
    const value = dateFromChangeEvent(event)
    if (value) setDuration(value.getTime() - Date.parse(getValues('start')))
  }

  const handleTypeChange = ({ target }) => {
    if (target.value) {
      setValue('title', null)
    }
  }

  return (
    <Grid container spacing={2}>
      <Grid size={{ md: 2 }}>
        <SelectionInput
          source='type'
          label='resources.appointments.fields.type'
          fullWidth
          emptyText='resources.appointments.types.none'
          onChange={handleTypeChange}
        />
      </Grid>
      {/* react-admin 5 ships DateInput/DateTimeInput, so the custom picker
          adapter is gone. The allDay split is kept deliberately: all-day
          appointments are stored at midnight, and a DateTimeInput would let a
          stray time creep into them. Same `start`/`end` timestamptz columns
          either way — this is a UI change only. */}
      <FormDataConsumer>
        {({ formData }) =>
          formData.allDay ? (
            <Grid size={{ md: 4 }}>
              <DateInput
                source='start'
                label='resources.appointments.fields.start'
                onChange={handleStartChange}
                validate={validateStart}
                fullWidth
              />
            </Grid>
          ) : (
            <Grid size={{ md: 4 }}>
              <DateTimeInput
                source='start'
                label='resources.appointments.fields.start'
                onChange={handleStartChange}
                validate={validateStart}
                fullWidth
              />
            </Grid>
          )
        }
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) =>
          formData.allDay ? (
            <Grid size={{ md: 4 }}>
              <DateInput
                source='end'
                label='resources.appointments.fields.end'
                onChange={handleEndChange}
                validate={validateEnd}
                fullWidth
              />
            </Grid>
          ) : (
            <Grid size={{ md: 4 }}>
              <DateTimeInput
                source='end'
                label='resources.appointments.fields.end'
                onChange={handleEndChange}
                validate={validateEnd}
                fullWidth
              />
            </Grid>
          )
        }
      </FormDataConsumer>
      <Grid size={{ md: 2 }}>
        <BooleanInput source='allDay' label='resources.appointments.fields.allDay' />
      </Grid>
      <Grid size={{ md: 6 }}>
        <ReferenceArrayInput
          source='boat_ids'
          reference='boats'
          enableGetChoices={({ q }) => (q ? q.length >= 1 : false)}
        >
          <AutocompleteArrayInput
            label='resources.appointments.fields.appointment_boat'
            optionText={(record) => record && `${record.model} (${record.name})`}
            shouldRenderSuggestions={(value) => value.length >= 1}
          />
        </ReferenceArrayInput>
      </Grid>
      <Grid size={{ md: 6 }}>
        <ReferenceArrayInput
          source='customer_ids'
          reference='customers'
          enableGetChoices={({ q }) => (q ? q.length >= 1 : false)}
        >
          <AutocompleteArrayInput
            label='resources.appointments.fields.appointment_customer'
            optionText={(record) =>
              record && record.id === '@@ra-create' ? record.name : customerName(record)
            }
            shouldRenderSuggestions={(value) => value.length >= 1}
            create={<CreateCustomer />}
          />
        </ReferenceArrayInput>
      </Grid>
      <Grid size={{ md: 12 }}>
        <ReferenceArrayInput
          source='user_ids'
          reference='users'
          perPage={100}
          sort={{ field: 'displayName', order: 'ASC' }}
        >
          <SelectArrayInput
            label='resources.appointments.fields.appointment_user'
            optionText={(user) => user?.displayName || user?.email}
            helperText='resources.appointments.fields.appointment_user_hint'
            fullWidth
          />
        </ReferenceArrayInput>
      </Grid>
      <Grid size={{ md: 6 }}>
        <FormDataConsumer>
          {({ formData }) => (
            <TextInput
              source='title'
              fullWidth
              label='resources.appointments.fields.title'
              validate={validateTitle}
              disabled={!!formData.type}
            />
          )}
        </FormDataConsumer>
      </Grid>
      <Grid size={{ md: 6 }}>
        <AutocompleteTextInput
          source='location'
          fullWidth
          label='resources.appointments.fields.location'
        />
      </Grid>
      <Grid size={{ md: 12 }}>
        <TextInput multiline source='notes' fullWidth label='resources.appointments.fields.notes' />
      </Grid>
    </Grid>
  )
}

export default UpsertAppointment
